/**
 * Map directions manager
 *
 * @author  Tamara Bazko
 */

import $ from 'jquery';
import EventTypes from 'components/EventTypes';
import Singleton from 'components/utils/Singleton';
import createLogger from 'components/logger/Logger';
import StoreFinderComponent from './StoreFinderComponent';
import device from 'components/utils/device';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import 'jquery.resizeEvents';
import 'jquery.extendPrototype';

const Logger = createLogger('DirectionsManager');

function DirectionsManager(settings) {
  Logger.info(module, 'Created');

  StoreFinderComponent.call(this, settings, module);

  this.Directions = this.settings.Directions;
  this.Map = this.settings.Map;

  this.$originInput = this.$element.find(this.settings.originInputSelector);
  this.$destinationInput = this.$element.find(this.settings.destinationInputSelector);
  this.$closeOverlayTrigger = this.$element.find(this.settings.closeOverlayTriggerSelector);
  this.$clearButton = this.$element.find(this.settings.clearButtonSelector);
  this.$travelModeBtn = this.$element.find(this.settings.travelModeBtnSelector);
  this.$error = this.$element.find(this.settings.errorSelector);
  this.$directionsList = document.getElementsByClassName('js-storeFinder-directions-list').jq;
  this.$gpsButton = document.getElementsByClassName('js-storeFinder-map-buttonGPS').jq;

  this.bindEvents();
}
$.extendPrototype(DirectionsManager, StoreFinderComponent.prototype, {
  $closeOverlayTrigger: undefined,
  $clearButton: undefined,
  $destinationInput: undefined,
  $directionsList: undefined,
  $error: undefined,
  $originInput: undefined,
  $sumbitBtn: undefined,
  activeMarker: undefined,
  currentLocationLabel: undefined,
  currentStore: undefined,
  newRouteDisplayed: undefined,
  latLngDestination: undefined,
  mapCanvas: undefined,
  originIcon: undefined,
  travelMode: 'driving',
  userLatLng: undefined,

  settings: {
    clearButtonSelector: '.js-storeFinder-directions-clearButton',
    closeOverlayTriggerSelector: '.js-storeFinder-directions-trigger',
    destinationInputSelector: '.js-storeFinder-directions-finish',
    errorSelector: '.js-storeFinder-directions-error',
    originInputSelector: '.js-storeFinder-directions-start',
    selector: '.js-storeFinder-directions',
    submitBtnSelector: '.js-storeFinder-directions-button',
    travelModeBtnSelector: '.js-storeFinder-directions-travelMode',
  },

  /**
   * Bind events
   */
  bindEvents() {
    document.jq
      .on(EventTypes.STOREFINDER_GET_USER_LOCATION, this.setUserLocation.bind(this))
      .on(EventTypes.DIRECTIONS_SHOW_OVERLAY, this.onDirectionsShowOverlay.bind(this))
      .on(EventTypes.DIRECTIONS_ROUTE_DISPLAYED, this.onRouteDisplayed.bind(this))
      .on(EventTypes.DIRECTIONS_PLACES_CHANGED, this.calculateAndDisplayRoute.bind(this))
      .on(EventTypes.DIRECTIONS_ROUTE_NOT_FOUND, this.onDirectionsRouteNotFound.bind(this));

    this.$clearButton.on('vclick', this.onClearButtonClick.bind(this));
    this.$originInput.on('keyup', this.onInputKeyUp.bind(this));
    this.$originInput.on('focus blur change keyup', this.onInputFocus.bind(this));
    this.$travelModeBtn.on('vclick', this.onTravelModeClick.bind(this));

    // Using 'click' instead of 'vclick' to avoid 'ghost' clicking on store details back trigger,
    // which is directly unerneath
    // https://api.jquerymobile.com/vclick/  (Check “Warning: Use vclick with caution”)
    this.$closeOverlayTrigger.on('click', this.closeDirectionsOverlay.bind(this));
  },

  /*
   * Update route
   */
  calculateAndDisplayRoute() {
    const origin = this.checkIfOriginChanged()
      ? this.$originInput.val()
      : this.$originInput.data('origin');

    if (!origin || origin === '') {
    } else {
      this.Directions.calculateAndDisplayRoute(origin, this.latLngDestination, this.travelMode);
    }
  },

  /*
   * Check if origin value was changed
   */
  checkIfOriginChanged() {
    const value = this.$originInput.val();

    if (this.userLatLng) {
      this.$originInput.data('origin', this.userLatLng);

      if (value === '') {
        this.$originInput.val(this.currentLocationLabel);
        return true;
      }
      if (value !== this.currentLocationLabel && value !== '') {
        return true;
      }
      return false;
    }
    if (value !== '') {
      if (!this.error) {
        this.Directions.showDirectionsDisplay();
      }
      return true;
    }
    this.setOriginMarker({ originLatLngHide: true });
    this.hideDirections();
    return false;
  },

  /*
   * Hide overlay, hide route, center map on current store
   */
  closeDirectionsOverlay() {
    if (!this.newRouteDisplayed) {
      this.$originInput.val('').focus(); // additional focus to remove suggested list
    }
    this.$originInput.blur();
    this.$gpsButton.off('vclick.directions');
    this.$closeOverlayTrigger.trigger(EventTypes.DIRECTIONS_HIDE_OVERLAY);
    this.$closeOverlayTrigger.trigger(EventTypes.DIRECTIONS_ROUTE_DISPLAYED, {
      bounds: undefined,
      originLatLngHide: true,
    });
    this.$element.addClass('is-hidden');
    this.hideDirections();
  },

  /*
   * Hide directions route and center on active marker
   */
  hideDirections() {
    this.Directions.hideDirectionsDisplay();
    this.Map.mapPanTo(this.latLngDestination);
  },

  /*
   * Hide error, if route was found or input was focused.
   * Show route.
   */
  hideError() {
    if (this.error) {
      this.error = false;
      this.$error.addClass('is-hidden');
      this.Directions.showDirectionsDisplay();
    }
  },

  /*
   * Showing directions overlay
   * Initializing Directions object
   * Setting route points (origin: current location or empty,  destination: store address)
   */
  initialize() {
    const settings = {
      inputElement: this.$originInput.get(0),
      map: this.Map.mapCanvas,
      directionsOverlay: this.$element,
      directionsList: this.$directionsList,
      storeFinder: this.$storeFinder,
    };

    // Preventing closing of store overlay and appearing of list on marker click
    window.jq.off(EventTypes.STOREFINDER_MARKER_CLICK);

    this.$element.removeClass('is-hidden');
    this.Directions.initialize(settings);
    this.setRoutePoints();
  },

  /**
   * Clear input field when user clicks on clear button
   */
  onClearButtonClick() {
    this.$originInput.val('').focus().trigger('change');
    this.$clearButton.removeClass('is-visible');
  },

  /*
   * If no route found: hide route, hide directions, hide origin marker,
   * and show error
   */
  onDirectionsRouteNotFound() {
    if (!this.error) {
      this.showError();
    }
  },

  /*
   * When directions are shown, store store data (coords, name)
   * and initialize
   * @param    {Object}    event   jQuery event object
   * @param    {Object}    eventData    Current store details, properties: {lat, lng, sogoulat, sogoulng, name}
   */
  onDirectionsShowOverlay(event, eventData) {
    this.currentStore = eventData;
    this.originIcon = this.Map.createMarkerIcon('origin');
    this.activeMarker = this.Map.activeMarker;

    this.initialize();
  },

  /*
   * Behaviour for GPS button if directions overlay is open.
   * Sets route from "Current location" to selected store.
   */
  onGpsButtonClick() {
    this.resetToDefault();
    this.Directions.calculateAndDisplayRoute(
      this.userLatLng,
      this.latLngDestination,
      this.travelMode
    );
  },

  /*
   * Clears input on focus. Uses current location and sets route
   * if user leaves input empty.
   * Removes error message on focus.
   */
  onInputFocus(event) {
    const eventType = event ? event.type : false;
    let value = this.$originInput.val();

    if (value === this.currentLocationLabel && eventType === 'focus') {
      this.$originInput.val('');
      value = '';
    } else if (eventType === 'blur') {
      if (device.hasTouch && matchBreakpoint(this.settings.breakpointOverlay)) {
        window.jq.scrollTop(0);
      }
      if (value === '') {
        this.$originInput.val(this.currentLocationLabel);
        value = this.currentLocationLabel;
        this.calculateAndDisplayRoute();
      }
    }

    const hasValue = !!(value && value !== '');
    this.$clearButton.toggleClass('is-visible', hasValue);
  },

  /*
   * If enter hit, find and show route
   * @param    {Object}    event   jQuery event object
   */
  onInputKeyUp(event) {
    if (event.keyCode !== 13) {
      this.newRouteDisplayed = false;
    }
  },

  /*
   * If route displayed, fit it in bounds (which also will also move it from
   * under the overlay)
   * @param    {Object}    event   jQuery event object
   * @param    {Object}    eventData    property: {bounds}
   */
  onRouteDisplayed(event, eventData) {
    if (eventData.bounds) {
      this.newRouteDisplayed = true;
      this.Map.fitBounds(eventData.bounds);
    }

    this.hideError();
    this.$gpsButton.off().on('vclick.directions', this.onGpsButtonClick.bind(this));
    this.setOriginMarker(eventData);
  },

  /*
   * Changing travel mode and route accordingly
   * @param    {Object}    event   jQuery event object
   */
  onTravelModeClick(event) {
    const $target = $(event.target);

    if ($target.hasClass('is-active')) {
    } else {
      $target.addClass('is-active');
      $target.siblings().removeClass('is-active');
      this.travelMode = $target.data('travelmode');
      this.calculateAndDisplayRoute();
    }
  },

  /*
   * Resets all values to correlate with "current location", when
   * it's provided. Reset used when gps button is clicked.
   */
  resetToDefault() {
    this.$originInput.val(this.currentLocationLabel);
  },

  /*
   * Set or hide origin marker (depends on: user location, search location, if
   * input is empty, if address typed in manually
   * @param    {Object}    data   {originLatLng, originLatLngHide}
   */
  setOriginMarker(data) {
    // Remove previous origin marker
    if (this.originMarker) {
      this.Map.setMap(this.originMarker, null);
    }

    // If overlay closed or input is empty
    if (data.originLatLngHide) {
      return;
    }

    // If address typed in manually or else user (or search) location is known
    // and should be used
    const originLatLng = data.originLatLng || this.userLatLng;

    this.originMarker = this.Map.getMarker(originLatLng, true, this.originIcon);
  },

  /*
   * If user location: set as starting point (origin), otherwise keep empty
   * Destination: store coords (as data), store name as value
   */
  setRoutePoints() {
    if (this.userLatLng) {
      this.$originInput.data('origin', this.userLatLng).val(this.currentLocationLabel);
      this.$clearButton.addClass('is-visible');
    } else if (this.$originInput.val() === '') {
      this.$originInput.focus();
    }

    this.latLngDestination = this.Map.getLatLng(this.currentStore.lat, this.currentStore.lng);
    this.$destinationInput.val(this.currentStore.name);
    this.calculateAndDisplayRoute();
  },

  /*
   * If got user location store it
   * @param    {Object}     event        jQuery event object
   * @param    {Object}     eventData    eventData coming from GPS or after searching for some place
   */
  setUserLocation(event, eventData) {
    const coords = eventData.coordinates;

    if (coords && !this.userLatLng && !eventData.address) {
      this.userLatLng = this.Map.getLatLng(coords.latitude, coords.longitude);
      this.currentLocationLabel = window.labels.storeFinder.currentLocation;
    }
  },

  /*
   * Show error, if route isn't found.
   * Hide origin marker and route.
   */
  showError() {
    this.$error.removeClass('is-hidden');
    this.error = true;
    this.setOriginMarker({ originLatLngHide: true });
    this.hideDirections();
  },
});

export default Singleton.create(DirectionsManager, 'DirectionsManager');
